// QuickStart.jsx
import React, { useState, useEffect, useRef } from 'react';
import './QuickStart.css';
import '../Home/Home.css';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTableList, getTablesByCategory, getAllCategories } from '../../context/TableListContext';
import airplaneImage from '../../assets/airplane.png';
import logo from '../../assets/logo.png';
import {
  FaTimes,
  FaBars,
  FaTable,
  FaGlobe,
  FaMobileAlt,
  FaSignOutAlt,
  FaQuestionCircle,
  FaUserCircle,
  FaChevronDown,
  FaChevronRight,
  FaChartPie,
} from 'react-icons/fa';

// Register Media Chrome custom elements
import 'media-chrome';

const tutorialVideos = {
  lounge: 'https://loungeimage.blob.core.windows.net/loungeatlas-portal/createLounge.mp4',
  country: 'https://loungeimage.blob.core.windows.net/loungeatlas-portal/createCountry.mp4',
  airport: 'https://loungeimage.blob.core.windows.net/loungeatlas-portal/createAirport.mp4',
};

const tutorialTitles = {
  lounge: 'Create a Lounge',
  country: 'Create a Country',
  airport: 'Create an Airport',
};

/**
 * Centered, robust video modal using Media Chrome.
 * - Controls never auto-hide.
 * - Single control bar (no native controls).
 * - Proper fullscreen sizing.
 */
function VideoModal({ open, onClose, src, title }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!open && videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch {}
    }
  }, [open]);

  if (!open) return null;

  const modalCSS = `
    /* Overlay and modal positioning */
    .qa-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.55);
      backdrop-filter: blur(2px);
      z-index: 2147483646;
    }
    .qa-modal {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: min(920px, 92vw);
      background: #fff;
      border-radius: 10px;
      padding: 12px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.45);
      z-index: 2147483647;
    }

    .qa-player-shell {
      width: 100%;
      aspect-ratio: 16 / 9;
      background: #000;
      border-radius: 8px;
      overflow: hidden;
      position: relative;
    }

    /* Make media-controller fill the shell */
    media-controller.qa-controller {
      display: block;
      width: 100%;
      height: 100%;
    }

    /* Make the video fill and letterbox/pillarbox properly */
    .qa-video {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
      background: #000;
    }

    /* Keep controls always visible */
    media-control-bar {
      opacity: 1 !important;
      visibility: visible !important;
      transition: none !important;
    }
    media-controller[inactive] media-control-bar {
      opacity: 1 !important;
      visibility: visible !important;
    }

    /* Fullscreen fixes: make controller + video fill the screen */
    :fullscreen media-controller.qa-controller,
    .qa-controller:-webkit-full-screen,
    .qa-controller:fullscreen {
      width: 100%;
      height: 100%;
    }
    :fullscreen .qa-video,
    .qa-video:-webkit-full-screen,
    .qa-video:fullscreen {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: modalCSS }} />
      <div className="qa-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="qa-modal" role="dialog" aria-modal="true" aria-label={title || 'Tutorial Video'}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{title || 'Tutorial'}</h2>
          <button className="close-icon" onClick={onClose} aria-label="Close video">
            <FaTimes size={20} />
          </button>
        </div>

        <div className="qa-player-shell">
          <media-controller class="qa-controller">
            <video
              ref={videoRef}
              slot="media"
              className="qa-video"
              src={src}
              playsInline
              preload="metadata"
              muted
              autoPlay
            >
              <source src={src} type="video/mp4" />
            </video>

            <media-control-bar>
              <media-play-button></media-play-button>
              <media-seek-backward-button seconds="10"></media-seek-backward-button>
              <media-seek-forward-button seconds="10"></media-seek-forward-button>

              <media-time-range></media-time-range>
              <media-time-display showduration></media-time-display>

              <media-mute-button></media-mute-button>
              <media-volume-range></media-volume-range>

              <media-playback-rate-button></media-playback-rate-button>
              <media-pip-button></media-pip-button>
              <media-fullscreen-button></media-fullscreen-button>
            </media-control-bar>
          </media-controller>
        </div>

        <div style={{ marginTop: 12 }}>
          <a href={src} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, textDecoration: 'underline' }}>
            Having trouble? Open in new tab / download
          </a>
        </div>
      </div>
    </>
  );
}

export default function QuickStart() {
  const [showHelp, setShowHelp] = useState(false);
  const [activePage, setActivePage] = useState('overview');
  const toggleHelp = () => setShowHelp((p) => !p);
  const { currentUser, logout } = useAuth();
  const { tableEndpoints } = useTableList();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [showAppManagementDropdown, setShowAppManagementDropdown] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Video modal state
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoSrc, setVideoSrc] = useState('');
  const [videoTitle, setVideoTitle] = useState('');

  const categories = getAllCategories();
  const categoryLabels = {
    data_management: 'Data Management',
    portal_management: 'Portal Management',
    sales_management: 'Sales Management',
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const openInNewTab = (url) => window.open(url, '_blank', 'noopener,noreferrer');
  const handleTableClick = (table) => navigate('/tables', { state: { selectedTable: table } });
  const toggleCategory = (category) => setExpandedCategory(expandedCategory === category ? null : category);

  const openTutorial = (key) => {
    const src = tutorialVideos[key];
    setVideoSrc(src);
    setVideoTitle(tutorialTitles[key] || 'Tutorial');
    setVideoOpen(true);
  };

  return (
    <div className="layout-wrapper">
      {/* Top Header */}
      <header className="top-bar" style={{ position: 'relative' }}>
        <div className="sidebar-top-row" style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
          {!collapsed && <img onClick={() => navigate("/home")} src={logo} alt="Logo" className="sidebar-logo tv-logo" />}
          <button
            className="menu-btn sidebar-menu-btn"
            onClick={() => setCollapsed((prev) => !prev)}
            aria-label="Toggle sidebar"
          >
            <FaBars size={24} />
          </button>
        </div>
        <h1 className="portal-title" style={{ margin: 0, position: 'absolute', left: 0, right: 0, textAlign: 'center', pointerEvents: 'none' }}>Lounge Atlas Portal</h1>
        <div style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="class1-btn" onClick={() => navigate('/profile')}>
            <FaUserCircle size={24} />
          </button>
          <button className="class1-btn" onClick={toggleHelp}>
            <FaQuestionCircle size={20} />
          </button>
        </div>
      </header>

      {/* Body */}
      <div className="body-section">
        {/* Sidebar */}
        <aside className={`side-panel ${collapsed ? 'collapsed' : ''}`} style={{ margin: 0 }}>
          {!collapsed && (
            <div className="sidebar-image">
              <img src={airplaneImage} alt="Airplane" />
            </div>
          )}

          <nav className="side-menu">
            {/* App Management Dropdown */}
            <div className="dropdown-container">
              <button className="dropdown-trigger" onClick={() => setShowAppManagementDropdown(!showAppManagementDropdown)}>
                <FaTable />
                {!collapsed && <span>App Management</span>}
                {!collapsed && <FaChevronDown className={`dropdown-arrow ${showAppManagementDropdown ? 'rotated' : ''}`} />}
              </button>

              {showAppManagementDropdown && !collapsed && (
                <div className="dropdown-menu">
                  {categories.map((category) => (
                    <div key={category} className="category-item">
                      <button className="category-header" onClick={() => toggleCategory(category)}>
                        <FaChevronRight className={`category-arrow ${expandedCategory === category ? 'rotated' : ''}`} />
                        <span>{categoryLabels[category] || category}</span>
                      </button>

                      {expandedCategory === category && (
                        <div className="table-list">
                          {getTablesByCategory(category).map((table) => (
                            <button key={table.path} className="table-item" onClick={() => handleTableClick(table)}>
                              <span>{table.tag}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Insights Page */}
            <button onClick={() => navigate('/insights')}>
              <FaChartPie />
              {!collapsed && <span>Insights</span>}
            </button>

            {/* Activity Log */}
            <button onClick={() => navigate('/activity-log')}>
              <FaUserCircle />
              {!collapsed && <span>Activity Log</span>}
            </button>

            <button onClick={() => openInNewTab('https://ieg-america.com/')}>
              <FaGlobe />
              {!collapsed && <span>IEG Website</span>}
            </button>
            <button onClick={() => openInNewTab('https://app.loungeatlas.com/')}>
              <FaMobileAlt />
              {!collapsed && <span>Lounge Atlas App</span>}
            </button>
            <button onClick={handleLogout}>
              <FaSignOutAlt />
              {!collapsed && <span>Sign Out</span>}
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="main-panel" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap', width: '100%' }}>
          {/* Side navigation */}
          <aside className="qs-side-nav">
            <h3 className="qs-side-title">Quick Start</h3>
            <button className={`qs-side-link ${activePage === 'overview' ? 'active' : ''}`} onClick={() => setActivePage('overview')} aria-current={activePage === 'overview'}>
              Welcome
            </button>
            <button className={`qs-side-link ${activePage === 'create-country' ? 'active' : ''}`} onClick={() => setActivePage('create-country')} aria-current={activePage === 'create-country'}>
              Create a Country
            </button>
            <button className={`qs-side-link ${activePage === 'create-airport' ? 'active' : ''}`} onClick={() => setActivePage('create-airport')} aria-current={activePage === 'create-airport'}>
              Create an Airport
            </button>
            <button className={`qs-side-link ${activePage === 'create-lounge' ? 'active' : ''}`} onClick={() => setActivePage('create-lounge')} aria-current={activePage === 'create-lounge'}>
              Create a Lounge
            </button>
            <div className="qs-side-sep" />

            <button
              className={`qs-side-link subtle ${activePage === 'open-app' ? 'active' : ''}`}
              onClick={() => setActivePage('open-app')}
              aria-current={activePage === 'open-app'}
            >
              Open App Management
            </button>

            <button
              className={`qs-side-link subtle ${activePage === 'open-insights' ? 'active' : ''}`}
              onClick={() => setActivePage('open-insights')}
              aria-current={activePage === 'open-insights'}
            >
              Open Insights
            </button>

            <button
              className={`qs-side-link subtle ${activePage === 'open-activity' ? 'active' : ''}`}
              onClick={() => setActivePage('open-activity')}
              aria-current={activePage === 'open-activity'}
            >
              Open Activity Log
            </button>

          </aside>

          {/* Content area */}
          <section className="qs-content">
            {activePage === 'overview' && (
              <div className="main-card">
                <div className="main-content">
                  <h2>Welcome {currentUser?.first_name || 'User'}!</h2>
                  <p className="dashboard-text">Here’s a quick guide to get you up and running with Lounge Atlas.</p>
                  <ul style={{ marginTop: 12 }}>
                    <li>Use App Management to browse and manage data tables</li>
                    <li>Search, sort, and edit records inline or via forms</li>
                    <li>Track changes in Activity Log</li>
                    <li>View trends and charts in Insights</li>
                  </ul>
                </div>
              </div>
            )}

            {activePage === 'create-country' && (
              <div className="qs-card">
                <h2>Create a Country</h2>
                <ol>
                  <li>Open App Management and select Data Management.</li>
                  <li>Choose the Country table.</li>
                  <li>Click Create and fill required fields (e.g., CountryID, CountryName, ICAOCode).</li>
                  <li>Save the record. Use search to verify it exists.</li>
                </ol>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button className="class1-btn" onClick={() => navigate('/tables')}>Go to Tables</button>
                  <button className="class1-btn" onClick={() => openTutorial('country')} aria-label="Watch country creation tutorial">
                    ▶ Watch Tutorial
                  </button>
                </div>
              </div>
            )}

            {activePage === 'create-airport' && (
              <div className="qs-card">
                <h2>Create an Airport</h2>
                <ol>
                  <li>Open App Management and select Data Management.</li>
                  <li>Choose the Airport table.</li>
                  <li>Click Create and fill required fields (e.g., AirportID, IATACode, APName).</li>
                  <li>Save the record. Link to Country if applicable.</li>
                </ol>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button className="class1-btn" onClick={() => navigate('/tables')}>Go to Tables</button>
                  <button className="class1-btn" onClick={() => openTutorial('airport')} aria-label="Watch airport creation tutorial">
                    ▶ Watch Tutorial
                  </button>
                </div>
              </div>
            )}

            {activePage === 'create-lounge' && (
              <div className="qs-card">
                <h2>Create a Lounge</h2>
                <ol>
                  <li>Open App Management and select Data Management.</li>
                  <li>Choose the Lounge table.</li>
                  <li>Click Create and fill key fields (LoungeName, AirportID, Country, City).</li>
                  <li>Optionally add Coordinates (lat,lng), MainImageURL, OperatingHours, and more.</li>
                  <li>Save the record, then add images/amenities under related tables if needed.</li>
                </ol>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button className="class1-btn" onClick={() => navigate('/tables')}>Go to Tables</button>
                  <button className="class1-btn" onClick={() => openTutorial('lounge')} aria-label="Watch lounge creation tutorial">
                    ▶ Watch Tutorial
                  </button>
                </div>
              </div>
            )}


            {activePage === 'open-app' && (
  <div className="qs-card">
    <h2>App Management</h2>
    <p style={{ marginTop: 6 }}>
      Manage all your data tables in one place—search, filter, edit, and create records with audit-friendly forms.
    </p>
    <ul style={{ marginTop: 10 }}>
      <li>Browse tables by category (Data / Portal / Sales Management)</li>
      <li>Inline edit or use the form view for full validation</li>
      <li>Create new records (e.g., Countries, Airports, Lounges)</li>
      <li>Verify changes using the Activity Log</li>
    </ul>
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
      <button className="class1-btn" onClick={() => navigate('/tables')}>
        Go to App Management
      </button>
    </div>
  </div>
)}

{activePage === 'open-insights' && (
  <div className="qs-card">
    <h2>Insights</h2>
    <p style={{ marginTop: 6 }}>
      Explore KPIs and charts to understand trends across lounges, airports, and operations.
    </p>
    <ul style={{ marginTop: 10 }}>
      <li>Quickly spot anomalies and recent changes</li>
      <li>Drill into categories and time ranges</li>
      <li>Share visuals with stakeholders</li>
    </ul>
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
      <button className="class1-btn" onClick={() => navigate('/insights')}>
        Open Insights
      </button>
    </div>
  </div>
)}

{activePage === 'open-activity' && (
  <div className="qs-card">
    <h2>Activity Log</h2>
    <p style={{ marginTop: 6 }}>
      Full audit trail of who changed what and when—essential for compliance and troubleshooting.
    </p>
    <ul style={{ marginTop: 10 }}>
      <li>Track creates, updates, and deletes across tables</li>
      <li>Access information like user, table, or time window</li>
      <li>Click through to the affected records</li>
    </ul>
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
      <button className="class1-btn" onClick={() => navigate('/activity-log')}>
        Open Activity Log
      </button>
    </div>
  </div>
)}

          </section>
        </main>
      </div>

      {showHelp && (
        <>
          <div className="overlay-backdrop" onClick={toggleHelp}></div>
          <div className="help-overlay">
            <h2>Need Help?</h2>
            <p><strong>Phone:</strong> +1 (514) 871-4910</p>
            <p><strong>Email:</strong> support@loungeatlas.com</p>
            <div style={{ marginTop: '1.5rem', textAlign: 'left' }}>
              <h3 style={{ fontSize: '1.1rem', color: '#0e265a', marginBottom: 6 }}>About Us</h3>
              <p style={{ margin: 0, fontSize: '1rem', color: '#333' }}>
                Lounge Atlas is a product of IEG Business Solutions, a company with nearly 30 years of experience building innovative, in-house software for the airline and airport industry. Our mission is to deliver excellence in business technology through close partnership with our clients and end-users.
              </p>
              <button
                style={{ marginTop: 10, background: '#1572dd', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', fontSize: '1rem', cursor: 'pointer' }}
                onClick={() => window.open('https://ieg-america.com/about', '_blank', 'noopener,noreferrer')}
              >
                More
              </button>
            </div>
            <button className="close-icon" onClick={toggleHelp}>
              <FaTimes size={20} />
            </button>
          </div>
        </>
      )}

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} src={videoSrc} title={videoTitle} />
    </div>
  );

}
